import { useState } from "react";
import {
  Activity,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Home,
  Hourglass,
  LayoutDashboard,
  Minus,
  Plus,
  TrendingUp,
  Upload,
  Wallet,
  XCircle,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardPageHeader } from "@/shared/ui/DashboardPageHeader";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { HospitalFinancialIndicators } from "../components/hospital-dashboard/HospitalFinancialIndicators";
import { HospitalInvoiceDistributionChart } from "../components/hospital-dashboard/HospitalInvoiceDistributionChart";
import { HospitalDashboardClientsSection } from "../components/hospital-dashboard/HospitalDashboardClientsSection";
import { DashboardMetricCard } from "../components/dashboard/DashboardMetricCard";
import { KpiGrid, type KpiTone } from "../components/KpiCard";
import {
  DashboardQuickAccess,
  type DashboardQuickAccessConfig,
} from "../components/dashboard/DashboardQuickAccess";
import { useHospitalDashboard } from "../hooks/useHospitalDashboard";
import { getRoleStatRoute } from "../navigation/dashboardRoutes";

const indicatorIcons = {
  file: FileText,
  upload: Upload,
  wallet: Wallet,
  clock: Clock,
  check: CheckCircle,
  x: XCircle,
  dollar: DollarSign,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  dollar: DollarSign,
  activity: Activity,
  trending: TrendingUp,
  x: XCircle,
};

const hospitalMetricConfig: Record<string, KpiTone> = {
  invoices: "sky",
  submitted: "amber",
  paid: "lime",
  review: "purple",
  approved: "mint",
  rejected: "red",
  revenue: "pink",
};

const hospitalQuickAccessConfig: Record<string, DashboardQuickAccessConfig> = {
  control: {
    icon: Plus,
    cardClassName:
      "!border-green !bg-green shadow-[0_8px_24px_rgba(184,228,23,0.22)] hover:!brightness-[0.97]",
    iconClassName: "bg-white/45 text-primary shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  settlements: {
    icon: Upload,
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "bg-primary text-white shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  invoices: {
    icon: FileText,
    cardClassName:
      "!border-primary/45 !bg-white hover:!border-primary/65 dark:!bg-white",
    iconClassName: "bg-primary text-white shadow-sm",
    labelClassName: "font-semibold text-primary",
  },
  portfolio: {
    icon: Home,
    cardClassName:
      "!border-yellow-400/50 !bg-yellow-400/20 hover:!bg-yellow-400/28 dark:!border-yellow-500/40 dark:!bg-yellow-500/15",
    iconClassName: "bg-yellow-400/30 text-amber-800 shadow-sm dark:text-primary",
    labelClassName: "font-semibold text-foreground",
  },
};

export function HospitalDashboardPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.dashboard.hospitalBoard;
  const { data, loading, error, usingApi } = useHospitalDashboard();
  const [activeQuickAccess, setActiveQuickAccess] = useState("control");

  const subtitle = labels.subtitle
    .replace("{invoices}", String(data.header.totalInvoices))
    .replace("{revenue}", data.header.monthlyRevenue);

  const quickAccessItems = data.quickActions.map((action) => ({
    id: action.id,
    href: action.href,
    label: labels.quickActions[action.id as keyof typeof labels.quickActions],
  }));

  return (
    <div className="space-y-5">
      {loading && <p className="text-sm text-muted">Chargement du tableau de bord…</p>}
      {error && <p className="text-sm text-danger">{error}</p>}
      {usingApi && !loading && (
        <p className="text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
      )}

      <DashboardPageHeader
        title={labels.pageTitle}
        subtitle={subtitle}
        icon={LayoutDashboard}
        badge={data.header.providerBadge}
        date={data.header.displayDate}
      />

      <div>
        <DashboardSectionLabel>{labels.sections.indicators}</DashboardSectionLabel>
        <KpiGrid className="grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {data.indicators.map((indicator) => {
            const Icon = indicatorIcons[indicator.icon];
            const FooterIcon = footerIcons[indicator.footerIcon];
            const label = labels.indicators[indicator.id as keyof typeof labels.indicators];
            const footer =
              labels.indicatorFooters[indicator.footerKey as keyof typeof labels.indicatorFooters];
            const tone = hospitalMetricConfig[indicator.id] ?? "slate";

            return (
              <DashboardMetricCard
                key={indicator.id}
                title={label}
                value={indicator.value}
                footer={
                  <span className="inline-flex items-center gap-1">
                    <FooterIcon size={12} />
                    {footer}
                  </span>
                }
                icon={Icon}
                href={getRoleStatRoute("hospital", indicator.id)}
                tone={tone}
              />
            );
          })}
        </KpiGrid>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.quickAccess}</DashboardSectionLabel>
        <DashboardQuickAccess
          items={quickAccessItems}
          configById={hospitalQuickAccessConfig}
          activeId={activeQuickAccess}
          onSelect={setActiveQuickAccess}
        />
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.charts}</DashboardSectionLabel>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <HospitalInvoiceDistributionChart data={data} />
          <HospitalFinancialIndicators data={data} />
        </div>
      </div>

      <div>
        <DashboardSectionLabel>{labels.sections.clients}</DashboardSectionLabel>
        <HospitalDashboardClientsSection data={data} />
      </div>
    </div>
  );
}
